import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BookIcon from '@mui/icons-material/Book';
import StyleIcon from '@mui/icons-material/Style';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SchoolIcon from '@mui/icons-material/School';
import ClassIcon from '@mui/icons-material/Class';
import supabase from '../integrations/supabase';
import { useAuth } from '../hooks/useAuth';
import { RAG_CONFIG } from '../config';
import apiService from '../services/api';
import AdvancedRAGTest from '../components/AdvancedRAGTest';
import DocumentProcessorTest from '../components/DocumentProcessorTest';
import RAGEnhancedAITest from '../components/RAGEnhancedAITest';
import { UploadSkeleton } from '../components/SkeletonLoader';

const Upload = () => {
  const { subject } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  
  // RAG-specific state
  const [selectedSubject, setSelectedSubject] = useState(subject || '');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedChapter, setSelectedChapter] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  const [processingStatus, setProcessingStatus] = useState({});
  
  // Available options
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableClasses, setAvailableClasses] = useState([]);
  const [availableChapters, setAvailableChapters] = useState([]);

  useEffect(() => {
    const initializeUpload = async () => {
      try {
        if (user) {
          // Load user's course selection to get available subjects
          const { data: courseSelection } = await supabase
            .from('course_selections')
            .select('*')
            .eq('user_id', user.id)
            .single();

          if (courseSelection) {
            const subjects = courseSelection.subjects || [];
            setAvailableSubjects(subjects);
            setAvailableClasses([courseSelection.class_level]);
            setSelectedClass(courseSelection.class_level);
            
            // Load chapters for the selected subject
            if (selectedSubject) {
              loadChaptersForSubject(selectedSubject, courseSelection.class_level);
            }
          }
        }
        setLoading(false);
      } catch (error) {
        console.error('Error initializing upload:', error);
        setLoading(false);
      }
    };

    initializeUpload();
  }, [user, selectedSubject]);

  const loadChaptersForSubject = async (subjectName, classLevel) => {
    try {
      // Use OpenAI API to generate chapters
      const response = await apiService.generateChapters(subjectName, classLevel);
      const chapters = response.chapters || [];
      setAvailableChapters(chapters);
    } catch (error) {
      console.error('Error loading chapters:', error);
      setAvailableChapters([]);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'text/plain',
      'text/csv',
      'application/json',
      'image/jpeg',
      'image/png',
      'image/webp'
    ];

    if (file.size > maxSize) {
      return { valid: false, error: 'File size exceeds 10MB limit' };
    }

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'File type not supported' };
    }

    return { valid: true };
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    const invalidFiles = [];

    fileArray.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push({
          id: Date.now() + Math.random(),
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'pending',
          progress: 0
        });
      } else {
        invalidFiles.push({ file, error: validation.error });
      }
    });

    if (invalidFiles.length > 0) {
      alert(`Some files were rejected:\n${invalidFiles.map(f => `${f.file.name}: ${f.error}`).join('\n')}`);
    }

    if (validFiles.length > 0) {
      setUploadedFiles(prev => [...prev, ...validFiles]);
      uploadFiles(validFiles);
    }
  };

  const uploadFiles = async (files) => {
    for (let fileObj of files) {
      try {
        setUploadProgress(prev => ({ ...prev, [fileObj.id]: 0 }));
        setProcessingStatus(prev => ({ ...prev, [fileObj.id]: 'uploading' }));

        // Step 1: Upload file to storage
        const formData = new FormData();
        formData.append('file', fileObj.file);
        formData.append('subject', selectedSubject);
        formData.append('class_level', selectedClass);
        formData.append('chapter', selectedChapter);
        formData.append('topic', selectedTopic);
        formData.append('title', fileObj.name);
        formData.append('user_id', user.id);
        formData.append('user_name', user.user_metadata?.full_name || user.email);

        // Simulate upload progress
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            const current = prev[fileObj.id] || 0;
            if (current < 90) {
              return { ...prev, [fileObj.id]: current + 10 };
            }
            return prev;
          });
        }, 200);

        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
          },
          body: formData
        });

        clearInterval(progressInterval);

        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }

        const result = await response.json();

        if (result.success) {
          setUploadProgress(prev => ({ ...prev, [fileObj.id]: 100 }));
          setProcessingStatus(prev => ({ ...prev, [fileObj.id]: 'processing' }));
          
          // Step 2: Process for RAG (extract text, create embeddings)
          await processForRAG(fileObj, result.file_id);
          
          setUploadedFiles(prev => prev.map(f => 
            f.id === fileObj.id 
              ? { ...f, status: 'completed', file_id: result.file_id }
              : f
          ));
          setProcessingStatus(prev => ({ ...prev, [fileObj.id]: 'completed' }));
        } else {
          throw new Error(result.error || 'Upload failed');
        }

      } catch (error) {
        console.error('Upload error:', error);
        setUploadedFiles(prev => prev.map(f => 
          f.id === fileObj.id 
            ? { ...f, status: 'error', error: error.message }
            : f
        ));
        setProcessingStatus(prev => ({ ...prev, [fileObj.id]: 'error' }));
      }
    }
  };

  const processForRAG = async (fileObj, fileId) => {
    try {
      // Call backend to process file for RAG
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/rag/process-document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_API_KEY || 'test-key'}`
        },
        body: JSON.stringify({
          file_id: fileId,
          subject: selectedSubject,
          class_level: selectedClass,
          chapter: selectedChapter,
          topic: selectedTopic,
          user_id: user.id
        })
      });

      if (!response.ok) {
        throw new Error('RAG processing failed');
      }

      const result = await response.json();
      console.log('RAG processing result:', result);

    } catch (error) {
      console.error('RAG processing error:', error);
      // Don't fail the upload, just log the error
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
    setProcessingStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[fileId];
      return newStatus;
    });
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return <ImageIcon className="text-4xl text-blue-500" />;
    if (fileType.startsWith('video/')) return <VideoLibraryIcon className="text-4xl text-red-500" />;
    if (fileType.startsWith('audio/')) return <AudioFileIcon className="text-4xl text-green-500" />;
    if (fileType === 'application/pdf') return <DescriptionIcon className="text-4xl text-red-600" />;
    if (fileType.includes('word')) return <DescriptionIcon className="text-4xl text-blue-600" />;
    return <DescriptionIcon className="text-4xl text-gray-500" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status, processingStatus) => {
    if (processingStatus === 'uploading') {
      return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>;
    }
    if (processingStatus === 'processing') {
      return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500"></div>;
    }
    if (status === 'completed') {
      return <CheckCircleIcon className="text-green-500" />;
    }
    if (status === 'error') {
      return <span className="text-red-500">⚠️</span>;
    }
    return null;
  };

  const getStatusText = (status, processingStatus) => {
    if (processingStatus === 'uploading') return 'Uploading...';
    if (processingStatus === 'processing') return 'Processing for RAG...';
    if (status === 'completed') return 'Completed';
    if (status === 'error') return 'Error';
    return 'Pending';
  };

  if (loading) {
    return <UploadSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg shadow-lg">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                aria-label="Go back"
              >
                <ArrowBackIcon className="text-gray-600 dark:text-gray-300" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Upload Documents for RAG
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Upload study materials to enhance AI responses with your content
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 text-sm font-medium">
                {user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Learner'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* RAG Configuration */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              📚 Document Configuration
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Subject Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <SchoolIcon className="inline mr-1" />
                  Subject
                </label>
                <select
                  value={selectedSubject}
                  onChange={(e) => {
                    setSelectedSubject(e.target.value);
                    setSelectedChapter('');
                    setSelectedTopic('');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select Subject</option>
                  {availableSubjects.map(subject => (
                    <option key={subject} value={subject}>{subject}</option>
                  ))}
                </select>
              </div>

              {/* Class Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <ClassIcon className="inline mr-1" />
                  Class
                </label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select Class</option>
                  {availableClasses.map(classLevel => (
                    <option key={classLevel} value={classLevel}>Class {classLevel}</option>
                  ))}
                </select>
              </div>

              {/* Chapter Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  📖 Chapter
                </label>
                <select
                  value={selectedChapter}
                  onChange={(e) => setSelectedChapter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={!selectedSubject}
                >
                  <option value="">Select Chapter</option>
                  {availableChapters.map(chapter => (
                    <option key={chapter} value={chapter}>{chapter}</option>
                  ))}
                </select>
              </div>

              {/* Topic Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  🏷️ Topic (Optional)
                </label>
                <input
                  type="text"
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                  placeholder="e.g., Kinematics, Algebra"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Upload Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div
            className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-200 ${
              dragActive
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              multiple
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              onChange={(e) => e.target.files && handleFiles(e.target.files)}
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.webp"
            />
            
            <div className="space-y-4">
              <CloudUploadIcon className="text-6xl text-purple-500 mx-auto" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Drop files here or click to upload
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Upload study materials for AI-enhanced learning
                </p>
                <div className="flex flex-wrap justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">PDF</span>
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">DOC/DOCX</span>
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">TXT</span>
                  <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">Images</span>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Max file size: 10MB • Files will be processed for RAG
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Upload Progress */}
        {uploadedFiles.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Upload Progress
            </h3>
            <div className="space-y-3">
              {uploadedFiles.map((fileObj) => (
                <motion.div
                  key={fileObj.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getFileIcon(fileObj.type)}
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {fileObj.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {formatFileSize(fileObj.size)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {getStatusIcon(fileObj.status, processingStatus[fileObj.id])}
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {getStatusText(fileObj.status, processingStatus[fileObj.id])}
                      </span>
                      
                      <button
                        onClick={() => removeFile(fileObj.id)}
                        className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <DeleteIcon className="text-gray-400 hover:text-red-500" />
                      </button>
                    </div>
                  </div>
                  
                  {(processingStatus[fileObj.id] === 'uploading' || processingStatus[fileObj.id] === 'processing') && (
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress[fileObj.id] || 0}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  
                  {fileObj.error && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                      Error: {fileObj.error}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Document Processor Test */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <DocumentProcessorTest />
        </motion.div>

        {/* Advanced RAG Test Component */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <AdvancedRAGTest />
        </motion.div>

        {/* RAG Enhanced AI Test Component */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <RAGEnhancedAITest />
        </motion.div>

        {/* RAG Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-2xl p-6"
        >
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
            🤖 RAG System Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                <strong>Embedding Model:</strong> {RAG_CONFIG.embedding_model}
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                <strong>Generation Model:</strong> {RAG_CONFIG.generation_model}
              </p>
              <p className="text-gray-600 dark:text-gray-400">
                <strong>Chunks Retrieved:</strong> {RAG_CONFIG.top_k_chunks}
              </p>
            </div>
            <div>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                <strong>Similarity Threshold:</strong> {RAG_CONFIG.similarity_threshold}
              </p>
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                <strong>Max Tokens:</strong> {RAG_CONFIG.max_tokens}
              </p>
              <p className="text-gray-600 dark:text-gray-400">
                <strong>Chunk Size:</strong> {RAG_CONFIG.chunk_size}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6"
        >
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <button
              onClick={() => navigate(`/study/${encodeURIComponent(selectedSubject || 'General')}`)}
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200"
            >
              <BookIcon className="text-2xl" />
              <div className="text-left">
                <div className="font-semibold">Study</div>
                <div className="text-sm opacity-90">View study materials</div>
              </div>
            </button>
            <button
              onClick={() => navigate(`/flashcards/${encodeURIComponent(selectedSubject || 'General')}`)}
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:from-blue-600 hover:to-purple-600 transition-all duration-200"
            >
              <StyleIcon className="text-2xl" />
              <div className="text-left">
                <div className="font-semibold">Flashcards</div>
                <div className="text-sm opacity-90">Review with flashcards</div>
              </div>
            </button>
            <button
              onClick={() => navigate(`/quiz/${encodeURIComponent(selectedSubject || 'General')}`)}
              className="flex items-center gap-3 p-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-200"
            >
              <AssignmentIcon className="text-2xl" />
              <div className="text-left">
                <div className="font-semibold">Quiz</div>
                <div className="text-sm opacity-90">Test your knowledge</div>
              </div>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Upload;